import { ReactNode } from 'react';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@trukt/ui/Loading';
import { ErrorComponent } from '@trukt/ui/ErrorComponent';
import { BaseApi } from '@trukt/utils/BaseApi';
import { MsalNavigationClient } from './MsalNavigationClient';
import { MsalTokenGuard } from './components/MsalTokenGuard';
import { MsalInstanceInterceptor } from './components/MsalInstanceInterceptor';
import { useInitialize } from './hooks/useInitialize';
import { MsalInstance } from './MsalInstance';

export interface MsalInstanceProviderProps {
  instance: MsalInstance;
  children: ReactNode;
  apis: BaseApi[];
}

export const MsalInstanceProvider = ({ instance, children, apis }: MsalInstanceProviderProps) => {
  const { isInitializing } = useInitialize(instance);
  const navigate = useNavigate();

  const navigationClient = new MsalNavigationClient(navigate);
  instance.setNavigationClient(navigationClient);

  if (isInitializing) {
    return <Loading fullscreen />;
  }

  return (
    <MsalProvider instance={instance}>
      <MsalAuthenticationTemplate
        authenticationRequest={{
          /**
           * critical for this to be here in order to not make duplicate
           * token requests when an api is called. Access tokens are tied to scope
           */
          scopes: instance.scopes,
        }}
        interactionType={InteractionType.Redirect}
        loadingComponent={() => <Loading fullscreen />}
        errorComponent={({ error }) => (
          <ErrorComponent error={error ?? new Error('Unable to login. Please contact support.')} />
        )}
      >
        <MsalTokenGuard>
          {children}
          <MsalInstanceInterceptor instance={instance} apis={apis} />
        </MsalTokenGuard>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
